import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Image as ImageIcon,
  Palette as PaletteIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  AutoAwesome as AutoAwesomeIcon,
  DeleteForever as DeleteForeverIcon,
} from '@mui/icons-material';
import { getAssets, uploadAsset, Asset } from '../services/api';
import BrandingConfiguration from './BrandingConfiguration';
import LogoWizard from './LogoWizard';

interface AssetManagerProps {
  tenantId?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`asset-tabpanel-${index}`}
      aria-labelledby={`asset-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export default function AssetManager({ tenantId }: AssetManagerProps) {
  const [tabValue, setTabValue] = useState(0);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState<Asset | null>(null);
  const [selectedIconType, setSelectedIconType] = useState<string | null>(null);
  const [logoWizardOpen, setLogoWizardOpen] = useState(false);

  // Allowed file types
  const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp', 'image/vnd.microsoft.icon', 'image/x-icon'];
  const maxFileSize = 5 * 1024 * 1024; // 5MB

  // Define required branding icons
  const brandingIcons = [
    { type: 'bannerLogo', label: 'Banner Logo', dimensions: '280×60px', description: 'Appears on sign-in page banner' },
    { type: 'squareLogoDark', label: 'Square Logo Dark', dimensions: '240×240px', description: 'Dark theme square logo' },
    { type: 'squareLogo', label: 'Square Logo', dimensions: '240×240px', description: 'Light theme square logo' },
    { type: 'favicon', label: 'Favicon', dimensions: '32×32px', description: 'Browser tab icon' },
    { type: 'headerLogo', label: 'Header Logo', dimensions: '300×100px', description: 'Header logo on pages' },
    { type: 'backgroundImage', label: 'Background Image', dimensions: '1920×1080px', description: 'Sign-in page background' },
  ];

  useEffect(() => {
    if (tenantId) {
      loadAssets();
    }
  }, [tenantId]);

  const loadAssets = async () => {
    if (!tenantId) return;
    
    try {
      setError(null);
      console.log('Loading assets for customer:', tenantId);
      const data = await getAssets(tenantId);
      console.log('Assets loaded:', data);
      setAssets(data || []);
    } catch (err: any) {
      console.error('Failed to load assets:', err);
      console.error('Error response:', err.response);
      // Only show error for non-404 errors (404 means endpoint exists but no data, which is fine)
      if (err.response?.status === 404) {
        setAssets([]);
      } else {
        setError(err.response?.data?.error || 'Failed to load assets');
      }
    }
  };

  const handleDeleteAll = async () => {
    if (!tenantId) return;
    
    try {
      const { deleteAllAssets } = await import('../services/api');
      const result = await deleteAllAssets(tenantId);
      
      if (result.success) {
        setUploadSuccess(true);
        setUploadError(null);
        await loadAssets();
        setTimeout(() => setUploadSuccess(false), 3000);
      }
    } catch (err: any) {
      setUploadError(err.response?.data?.error || 'Failed to delete assets');
    }
  };

  const handleFile = async (file: File) => {
    if (!tenantId) return;
    
    setUploadError(null);
    setUploadSuccess(false);

    // Validate file type
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Invalid file type. Please upload PNG, JPG, SVG, WEBP, or ICO files.');
      return;
    }

    // Validate file size
    if (file.size > maxFileSize) {
      setUploadError('File size exceeds 5MB limit.');
      return;
    }

    // Check if icon type is selected
    if (!selectedIconType) {
      setUploadError('Please select an icon type before uploading.');
      return;
    }

    try {
      setUploading(true);

      // Read file as base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        if (e.target?.result) {
          const base64Content = (e.target.result as string).split(',')[1];
          
          // Upload with icon type as AssetType (convert camelCase to PascalCase)
          const assetTypePascal = selectedIconType 
            ? selectedIconType.charAt(0).toUpperCase() + selectedIconType.slice(1)
            : undefined;
          await uploadAsset(tenantId, file.name, base64Content, file.type, assetTypePascal);
          
          setUploadSuccess(true);
          setTimeout(() => setUploadSuccess(false), 3000);
          
          // Clear selection
          setSelectedIconType(null);
          
          // Reload assets
          await loadAssets();
        }
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      setUploadError(err.response?.data?.error || 'Failed to upload asset');
    } finally {
      setUploading(false);
    }
  };

  const handleIconCardClick = (iconType: string) => {
    // Select this icon type for upload (allows replacing existing icons)
    setSelectedIconType(iconType);
    
    // Trigger file input
    const fileInput = document.getElementById('file-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  };

  const isIconUploaded = (iconType: string): boolean => {
    const found = assets.some(a => 
      a.assetType?.toLowerCase() === iconType.toLowerCase() || 
      a.assetName?.toLowerCase() === iconType.toLowerCase() ||
      a.fileName?.toLowerCase().includes(iconType.toLowerCase())
    );
    console.log(`isIconUploaded(${iconType}):`, found, 'Assets:', assets.map(a => ({ type: a.assetType, name: a.assetName })));
    return found;
  };

  const getIconAsset = (iconType: string): Asset | undefined => {
    const asset = assets.find(a => 
      a.assetType?.toLowerCase() === iconType.toLowerCase() || 
      a.assetName?.toLowerCase() === iconType.toLowerCase() ||
      a.fileName?.toLowerCase().includes(iconType.toLowerCase())
    );
    console.log(`getIconAsset(${iconType}):`, asset);
    return asset;
  };

  const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await handleFile(files[0]);
      // Reset input so same file can be selected again
      e.target.value = '';
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAssetToDelete(null);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (!tenantId) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="warning">
          Please select a customer to manage assets and branding.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <PaletteIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4">Branding & Logos</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage customer branding assets and M365 customization
            </Typography>
          </Box>
        </Box>

        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab label="Logos" />
          <Tab label="Branding" />
        </Tabs>

      {/* Icons Tab */}
      <TabPanel value={tabValue} index={0}>
        {/* Hidden file input */}
        <input
          type="file"
          id="file-upload"
          accept=".png,.jpg,.jpeg,.svg"
          style={{ display: 'none' }}
          onChange={handleFileInput}
          disabled={uploading}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {uploadSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Icon uploaded successfully!
          </Alert>
        )}

        {uploadError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {uploadError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h5" gutterBottom>
              Required Branding Logos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click on a logo card to upload or replace the image. All logos are required for complete M365 branding.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Button
              variant="contained"
              startIcon={<AutoAwesomeIcon />}
              onClick={() => setLogoWizardOpen(true)}
              sx={{ minWidth: 200 }}
            >
              Generate from Logo
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteForeverIcon />}
              onClick={handleDeleteAll}
              disabled={assets.length === 0}
              sx={{ minWidth: 200 }}
            >
              Delete All Icons
            </Button>
          </Box>
        </Box>

        {/* Icon Grid - 2 columns x 3 rows */}
        <Grid container spacing={3}>
          {brandingIcons.map((icon) => {
            const isUploaded = isIconUploaded(icon.type);
            const asset = getIconAsset(icon.type);

            return (
              <Grid item xs={12} md={6} key={icon.type}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    border: isUploaded ? '2px solid #4caf50' : '2px solid #e0e0e0',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: isUploaded ? '#4caf50' : '#1976d2',
                      boxShadow: 3,
                    },
                    position: 'relative',
                  }}
                  onClick={() => handleIconCardClick(icon.type)}
                >
                  {/* Checkmark badge */}
                  {isUploaded && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        zIndex: 1,
                      }}
                    >
                      <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 32 }} />
                    </Box>
                  )}

                  <CardMedia
                    component="div"
                    sx={{
                      height: 180,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f5f5f5',
                      backgroundImage: 'linear-gradient(45deg, #e0e0e0 25%, transparent 25%), linear-gradient(-45deg, #e0e0e0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e0e0e0 75%), linear-gradient(-45deg, transparent 75%, #e0e0e0 75%)',
                      backgroundSize: '20px 20px',
                      backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
                    }}
                  >
                    {asset && asset.blobUrl ? (
                      <img
                        key={asset.assetID}
                        src={`${asset.blobUrl}&t=${Date.now()}`}
                        alt={icon.label}
                        style={{
                          maxWidth: '90%',
                          maxHeight: '90%',
                          objectFit: 'contain',
                        }}
                        onError={(e) => {
                          console.error(`Failed to load image for ${icon.label}:`, asset.blobUrl);
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <Box sx={{ textAlign: 'center' }}>
                        {isUploaded ? (
                          <ImageIcon sx={{ fontSize: 64, color: '#4caf50' }} />
                        ) : (
                          <AddIcon sx={{ fontSize: 64, color: '#bdbdbd' }} />
                        )}
                      </Box>
                    )}
                  </CardMedia>

                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                      <Typography variant="h6" component="div">
                        {icon.label}
                      </Typography>
                    </Box>
                    
                    <Chip
                      label={icon.dimensions}
                      size="small"
                      color={isUploaded ? 'success' : 'default'}
                      sx={{ mb: 1 }}
                    />

                    <Typography variant="body2" color="text.secondary">
                      {icon.description}
                    </Typography>

                    {asset && (
                      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          File: {asset.fileName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Uploaded: {new Date(asset.uploadedDate).toLocaleDateString()}
                        </Typography>
                      </Box>
                    )}

                    {!isUploaded && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="error">
                          Not uploaded yet
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Summary */}
        <Paper sx={{ p: 3, mt: 4, backgroundColor: '#f5f5f5' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">
                Upload Status: {brandingIcons.filter(icon => isIconUploaded(icon.type)).length} of {brandingIcons.length} logos uploaded
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {brandingIcons.every(icon => isIconUploaded(icon.type))
                  ? '✓ All required logos have been uploaded' 
                  : `Upload ${brandingIcons.filter(icon => !isIconUploaded(icon.type)).length} more logo(s) to complete your branding`}
              </Typography>
            </Box>
            {brandingIcons.every(icon => isIconUploaded(icon.type)) && (
              <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 48 }} />
            )}
          </Box>
        </Paper>

        {/* Next Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button 
            variant="contained" 
            onClick={() => setTabValue(1)}
          >
            Next: Branding Settings
          </Button>
        </Box>

      </TabPanel>

      {/* Branding Tab */}
      <TabPanel value={tabValue} index={1}>
        <BrandingConfiguration tenantId={tenantId} />
      </TabPanel>

      {/* Dialogs - Outside of TabPanels */}
      {tenantId && (
        <LogoWizard
          open={logoWizardOpen}
          onClose={() => setLogoWizardOpen(false)}
          tenantId={tenantId}
          onSuccess={() => {
            loadAssets();
            setUploadSuccess(true);
            setTimeout(() => setUploadSuccess(false), 3000);
          }}
        />
      )}

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Asset</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{assetToDelete?.fileName}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} variant="contained">OK</Button>
        </DialogActions>
      </Dialog>
      </Paper>
    </Container>
  );
}
